import { useRef, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface InteractionEvent {
  event_type: string;
  session_id?: string;
  duration_ms?: number;
  metadata?: Record<string, unknown>;
  occurred_at?: string;
}

/**
 * Hook to log learner interaction events for the AI Coach.
 * Call log() for discrete events (modality_selected, sense_check_answered, exam_submitted, etc.).
 * Automatically logs node_opened on mount and node_closed on unmount.
 */
export function useInteractionLogger(courseId: string | undefined, nodeId: string) {
  const sessionId = useRef(crypto.randomUUID()).current;
  const startTime = useRef(Date.now());
  const pendingEvents = useRef<InteractionEvent[]>([]);

  const log = useCallback(
    (event_type: string, metadata?: Record<string, unknown>) => {
      pendingEvents.current.push({
        event_type,
        session_id: sessionId,
        metadata,
        occurred_at: new Date().toISOString(),
      });
    },
    []
  );

  const flush = useCallback(async () => {
    if (!courseId || pendingEvents.current.length === 0) return;
    const events = pendingEvents.current.splice(0);
    try {
      await supabase.functions.invoke("log-interaction", {
        body: { courseId, nodeId, events },
      });
    } catch (e) {
      console.warn("[useInteractionLogger] flush failed:", e);
    }
  }, [courseId, nodeId]);

  useEffect(() => {
    if (!courseId) return;
    log("node_opened");

    const handleBeforeUnload = () => {
      pendingEvents.current.push({
        event_type: "node_closed",
        session_id: sessionId,
        duration_ms: Date.now() - startTime.current,
        occurred_at: new Date().toISOString(),
      });
      flush();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      pendingEvents.current.push({
        event_type: "node_closed",
        session_id: sessionId,
        duration_ms: Date.now() - startTime.current,
        occurred_at: new Date().toISOString(),
      });
      flush();
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [courseId, nodeId, log, flush]);

  return { log, flush };
}
